import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, PropsWithChildren } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import type { AuthConfig } from '@bettercomp/ams-spa-auth';
import { AuthContext, useAmsAuthSession, AuthContextValue } from '@bettercomp/ams-spa-auth-react';
import { amsPublicApiConfigGetAuthConfig, DEV_GRANTS_STORAGE_KEY } from 'service/amsApi';
import { hasAmsSessionArtifact, setAmsSession } from 'service/amsSession';
import { loadUserData, setUserData } from 'redux/user';
import { selectUser } from 'redux/user/selectors';
import { TypedDispatch } from 'redux/configureStore';
import { reportError } from 'utils/reporting';

type AmsAdoptionContextValue = {
  /**
   * Arms the AMS session machinery for a fresh login: the login callback calls
   * this after the feature flag confirms the caller's company is on AMS auth,
   * making the provider fetch config and construct the session it will then
   * bootstrap. Idempotent.
   */
  requestAdoption: () => void;
};

const AmsAdoptionContext = createContext<AmsAdoptionContextValue | undefined>(undefined);

export function useAmsAdoption(): AmsAdoptionContextValue {
  const context = useContext(AmsAdoptionContext);
  if (!context) {
    throw new Error('useAmsAdoption must be used within AmsAuthProvider');
  }
  return context;
}

// ===== REMOVE IN S11 =====
function hasDevIdentity(): boolean {
  return (
    process.env.NODE_ENV === 'development' &&
    typeof window !== 'undefined' &&
    Boolean(localStorage.getItem(DEV_GRANTS_STORAGE_KEY))
  );
}
// ===== REMOVE IN S11 =====

/**
 * App-local owner of the AMS auth session. Stands fully down (no config fetch,
 * no session) unless this browser holds an AMS session artifact (amsAuth
 * cookie — reload of an AMS session) or the login callback requested adoption;
 * legacy-only environments therefore never touch /ams/*.
 */
export function AmsAuthProvider({ children }: PropsWithChildren) {
  const dispatch = useDispatch<TypedDispatch>();
  const user = useSelector(selectUser);
  const [devBypass] = useState(hasDevIdentity);
  const [hasArtifact] = useState(hasAmsSessionArtifact);
  const [adoptionRequested, setAdoptionRequested] = useState(false);

  const requestAdoption = useCallback(() => setAdoptionRequested(true), []);

  const enabled = (hasArtifact || adoptionRequested) && !devBypass;

  const configQuery = useQuery({
    queryKey: ['ams-auth-config'],
    queryFn: async () => {
      // Report both failure shapes with their cause — an HTTP error body and a
      // thrown network error — so on-call can tell an outage from a contract
      // break without reproducing locally; the UI only ever sees configError.
      try {
        const { data, error } = await amsPublicApiConfigGetAuthConfig();
        if (error) {
          throw new Error(`Failed to fetch the AMS auth config: ${JSON.stringify(error)}`);
        }
        return data;
      } catch (err) {
        reportError(err);
        throw err;
      }
    },
    staleTime: Infinity,
    retry: false,
    enabled,
  });

  const configError = enabled && Boolean(configQuery.error);

  const config = useMemo<AuthConfig | undefined>(
    () =>
      configQuery.data
        ? {
            hostedUiUrl: configQuery.data.hosted_ui_url,
            clientId: configQuery.data.client_id,
            exchangeUrl: configQuery.data.exchange_url,
          }
        : undefined,
    [configQuery.data],
  );

  // Unlike the AMS SPAs, no onRenewed callback and no signInRequired
  // subscription anywhere in app/: recovery happens at the transport layer
  // (fetchWithRenewal retries the failed request itself), which reaches every
  // client stack — most of app/ is not react-query, so the SPAs'
  // signal-then-invalidate model would miss the majority of callers.
  // bootstrapOnCallback: no CallbackView owns the exchange here — the login
  // callback only seeds the amsAuth cookie and arms adoption, so the session
  // must mint itself even while the URL still carries the Cognito code.
  const { session, outcome, reportOutcome, retryBootstrap } = useAmsAuthSession({
    config,
    enabled,
    bootstrapOnCallback: true,
  });

  useEffect(() => {
    setAmsSession(session);
    return () => setAmsSession(undefined);
  }, [session]);

  // Reload of an AMS session: bootstrap mints before any user is in Redux, so
  // the provider triggers the load. Fresh logins arrive with the user already
  // loaded by the callback's legacy-first sequence, and the ref keeps renewals
  // (which also publish minted) from re-dispatching.
  const userLoadRequestedRef = useRef(false);
  useEffect(() => {
    if (outcome?.kind === 'minted' && !user && !userLoadRequestedRef.current) {
      userLoadRequestedRef.current = true;
      dispatch(loadUserData());
    }
    if (outcome?.kind === 'signedOut') {
      userLoadRequestedRef.current = false;
      dispatch(setUserData(null));
    }
  }, [outcome, user, dispatch]);

  const value = useMemo<AuthContextValue>(
    () => ({
      outcome,
      configError,
      devBypass,
      session,
      reportOutcome,
      retry: () => {
        if (configError) {
          void configQuery.refetch();
          return;
        }
        retryBootstrap();
      },
    }),
    [outcome, configError, devBypass, session, reportOutcome, retryBootstrap, configQuery],
  );

  const adoptionValue = useMemo<AmsAdoptionContextValue>(() => ({ requestAdoption }), [requestAdoption]);

  return (
    <AuthContext.Provider value={value}>
      <AmsAdoptionContext.Provider value={adoptionValue}>{children}</AmsAdoptionContext.Provider>
    </AuthContext.Provider>
  );
}
